import { Button, type FrameArtwork, FrameDrawer, Icon } from '@design-system';
import { resolveShopifyAdminProductId } from '@utils/shopifyIds';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { PRODUCTS_PAGE_ROOT_PATH } from '../../../constants';
import { useProductCheckoutLink } from '../../../features/checkout/hooks/useProductCheckoutLink';
import { withFramingChoice } from '../../../features/checkout/utils/framingQuery';
import { purchasingState } from '../../../helpers/purchasing';
import { useDrawStage } from '../../../hooks/useDrawStage';
import { useDraws } from '../../../providers/DrawProvider';
import type { DrawStage } from '../../../types/Draw';
import type {
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import type { Frame } from '../../../types/Products';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
  PurchasingState,
} from '../../../types/Purchasing';
import type { FrameDraft } from '../../Checkout/Framing/hooks/useFrameDraft';
import {
  FrameSelection,
  hasFramingOptions,
  toFrameOptions,
  usePurchaseDetails,
} from '../../ContentBlockFramingPreviewer';
import { Modal } from '../../Dialog/Modal';

/** Draw stages the page offers an entry for; draws stay "Coming soon" in
    Shopify while open, so their Shopify state can't tell. */
const OPEN_DRAW_STAGES: DrawStage[] = [
  'DRAW',
  'DRAW_PRE_ORDER_CLOSED',
  'DRAW_PRE_ORDER_OPEN',
  'FCFS_PRE_ORDER_OPEN',
];
const CLOSED_DRAW_STAGES: DrawStage[] = ['UPCOMING', 'SOLD_OUT'];

const UNAVAILABLE_STATES: PurchasingState[] = [
  'COMING_SOON',
  'SOLD_OUT',
  'UNAVAILABLE',
];

/** `ModalCover` holds for its 300ms timeout, then slides out over 300ms. */
const MODAL_EXIT_MS = 600;

const noop = () => undefined;

/** A work in the modal's dropdown, with its product page slug. */
type FramingArtwork = FrameArtwork & { slug?: string };

/** A story in the product's variant list, as the product page loads it. */
type VariantStory = {
  name?: string;
  slug?: string;
  content?: {
    shopifyId?: string;
    id?: Array<{ id?: string }>;
    images?: Array<{
      image?: { filename?: string };
      coverImage?: { filename?: string };
    }>;
  };
};

/**
 * The product page's own variant list, the works its variant selector links
 * between. Variants store their Shopify id as `shopifyId` (product_v2) or in
 * the `id` field (product).
 */
const toVariantArtworks = (
  product: ProductStoryblok | ProductV2Storyblok,
): FramingArtwork[] => {
  const variants = (product.productVariants?.[0]?.variants ??
    []) as unknown as VariantStory[];

  return variants.flatMap((story) => {
    const id = resolveShopifyAdminProductId(
      story?.content?.shopifyId ?? story?.content?.id?.[0]?.id,
    );
    if (!id || !story.slug) return [];

    const asset = story.content?.images?.[0];
    const title = story.name ?? '';

    return [
      {
        id,
        slug: story.slug,
        title,
        thumbnail: {
          src: asset?.image?.filename ?? asset?.coverImage?.filename ?? '',
          alt: title,
        },
      },
    ];
  });
};

/**
 * Continuing from the modal opens checkout with the framing already chosen
 * for this work, whatever the release: a draw, a multi-work draw or a direct
 * purchase. A work the checkout doesn't take keeps the choice on the page, for
 * its own CTA to hand to Shopify. Another work picked in the dropdown
 * (`otherWorkSlug`) takes the choice to that work's checkout instead, or to
 * its product page when the checkout doesn't take this one.
 */
const useContinueWithFraming = <T extends ProductPurchaseDetails>(
  purchasingController: ProductPurchasingController<T>,
  inCollectionSet: boolean,
) => {
  const router = useRouter();
  const checkoutLink = useProductCheckoutLink();
  const [loading, setLoading] = useState(false);

  const continueWith = async (frame: Frame | null, otherWorkSlug?: string) => {
    if (otherWorkSlug) {
      setLoading(true);
      await router.push(
        checkoutLink
          ? withFramingChoice(`/checkout/${otherWorkSlug}`, frame)
          : `/${PRODUCTS_PAGE_ROOT_PATH}/${otherWorkSlug}`,
      );
      return true;
    }

    // A frame for this work alone doesn't fit a collection set purchase.
    if (!inCollectionSet) purchasingController.setFrame(frame);
    if (!checkoutLink) return false;

    setLoading(true);
    await router.push(withFramingChoice(checkoutLink, frame));
    return true;
  };

  return { continueWith, loading };
};

/**
 * The modal's selection, with the same works as the release page: the
 * product, then its variants. The page's own work uses the page's frame
 * options and current frame; another work loads its own from Shopify, and its
 * actions carry the choice to that work (`otherWorkSlug`).
 */
const ProductFramingSelection = ({
  currency,
  currentArtwork,
  currentFrame,
  frameOptions,
  onClose,
  productImage,
  renderActions,
  variants,
}: {
  currency: ProductPurchaseDetails['currency'];
  currentArtwork: FrameArtwork;
  currentFrame?: Frame | null;
  frameOptions: NonNullable<
    ProductPurchasingController<ProductPurchaseDetails>['frameOptions']
  >;
  onClose: () => void;
  productImage?: string | null;
  renderActions?: (draft: FrameDraft, otherWorkSlug?: string) => ReactNode;
  variants: FramingArtwork[];
}) => {
  const [selectedId, setSelectedId] = useState(currentArtwork.id);
  const isCurrent = selectedId === currentArtwork.id;
  const otherWorks = variants.filter(({ id }) => id !== currentArtwork.id);
  // Every other work's details, so works without framing stay out of the
  // dropdown.
  const detailsById = usePurchaseDetails(otherWorks.map(({ id }) => id));
  const other = isCurrent ? undefined : detailsById[selectedId];
  const otherFrameOptions = useMemo(
    () => (other ? toFrameOptions(other) : []),
    [other],
  );
  const artworks: FramingArtwork[] = [
    currentArtwork,
    ...otherWorks.filter(({ id }) => hasFramingOptions(detailsById[id])),
  ];
  const otherWorkSlug = isCurrent
    ? undefined
    : artworks.find(({ id }) => id === selectedId)?.slug;

  // Another work loading, or failed to load: keep the dropdown so a work can
  // still be picked.
  if (!isCurrent && !other) {
    return (
      <FrameDrawer
        artworks={artworks}
        groups={[]}
        isLoading={other === undefined}
        layout="modal"
        onArtworkChange={setSelectedId}
        onClose={onClose}
        onSelect={noop}
        selectedArtworkId={selectedId}
        total={0}
      />
    );
  }

  return (
    <FrameSelection
      key={selectedId}
      artworks={artworks}
      currencyCode={other?.currency ?? currency}
      currentSelection={isCurrent ? currentFrame : null}
      frameOptions={isCurrent ? frameOptions : otherFrameOptions}
      layout="modal"
      onArtworkChange={setSelectedId}
      onClose={onClose}
      productImage={other ? other.image : productImage}
      // Without a page to go to, another work can be previewed but not taken on.
      renderActions={
        renderActions && (isCurrent || otherWorkSlug)
          ? (draft) => renderActions(draft, otherWorkSlug)
          : undefined
      }
      selectedArtworkId={selectedId}
    />
  );
};

/**
 * "Customise your frame": opens the framing previewer over the product page
 * and writes the confirmed choice back to the page's purchase.
 */
export const ProductFramingCustomise = <T extends ProductPurchaseDetails>({
  product,
  purchasingController,
}: {
  product: ProductStoryblok | ProductV2Storyblok;
  purchasingController: ProductPurchasingController<T>;
}) => {
  const { t } = useTranslation('framing');
  const [open, setOpen] = useState(false);
  // "Enter draw for set" selects the whole set; framing here stays about this
  // work on its own.
  const inCollectionSet =
    (purchasingController.collectionSet?.filter(({ selected }) => selected)
      .length ?? 0) > 1;
  const { continueWith, loading } = useContinueWithFraming(
    purchasingController,
    inCollectionSet,
  );
  const { mainDraw } = useDraws();
  const drawStage = useDrawStage(mainDraw?.draw);
  // Each opening starts a fresh draft. The content outlives `open` until the
  // modal has finished sliding out, so it leaves as it arrived.
  const [session, setSession] = useState(0);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (open) return undefined;
    const timeout = setTimeout(() => setMounted(false), MODAL_EXIT_MS);
    return () => clearTimeout(timeout);
  }, [open]);
  const { product: details } = purchasingController;
  const frameOptions =
    purchasingController.productFrameOptions ??
    purchasingController.frameOptions;

  if (!details || !frameOptions?.length) return null;

  const image = product.images?.[0];
  const artwork = {
    // Admin ids, as the release page's artwork list uses.
    id: resolveShopifyAdminProductId(details.shopifyId) ?? details.shopifyId,
    title: details.name,
    thumbnail: {
      src:
        details.image ??
        (image?.component === 'image'
          ? image.image?.filename
          : image?.coverImage?.filename) ??
        '',
      alt: details.name,
    },
  };
  const close = () => setOpen(false);
  // The modal stays up, loading, while checkout takes over.
  const continueAndClose = (frame: Frame | null, otherWorkSlug?: string) => {
    void continueWith(frame, otherWorkSlug).then((handedOff) => {
      if (!handedOff) close();
    });
  };
  // Nothing to confirm on a work that can't be bought yet, or any more.
  const { closingDate, releaseType } = product as unknown as ProductType;
  const state = purchasingState({
    available: details.available,
    closingDate,
    releaseType,
    tags: details.tags,
  });
  const canChoose =
    mainDraw && OPEN_DRAW_STAGES.includes(drawStage)
      ? true
      : !(mainDraw && CLOSED_DRAW_STAGES.includes(drawStage)) &&
        !UNAVAILABLE_STATES.includes(state);

  return (
    <>
      <button
        className="flex w-full items-center gap-3 text-left text-neutral-4"
        disabled={purchasingController.loading}
        onClick={() => {
          setSession((count) => count + 1);
          setMounted(true);
          setOpen(true);
        }}
        type="button"
      >
        <span className="flex-1">{t('previewer.customise')}</span>
        <Icon name="ic_chevron_right" size="sm" />
      </button>

      <Modal
        dismissible
        onClose={close}
        open={open}
        title={t('title')}
        verticalAlign="flex-end"
      >
        {mounted ? (
          <div
            key={session}
            className="mt-[4.375rem] h-[calc(100dvh-4.375rem)] w-full overflow-y-auto overscroll-none rounded-t-lg [&>*]:min-h-full"
          >
            <ProductFramingSelection
              currency={details.currency}
              currentArtwork={artwork}
              currentFrame={
                purchasingController.productFrame === undefined
                  ? purchasingController.frame
                  : purchasingController.productFrame
              }
              frameOptions={frameOptions}
              onClose={close}
              productImage={details.image}
              variants={toVariantArtworks(product)}
              renderActions={
                canChoose
                  ? (draft, otherWorkSlug) => (
                      <>
                        <Button
                          disabled={!draft.draftFrame || loading}
                          fullWidth
                          label={t('previewer.confirm')}
                          loading={loading}
                          onClick={() =>
                            continueAndClose(draft.draftFrame, otherWorkSlug)
                          }
                          variant="primary"
                        />
                        <Button
                          disabled={loading}
                          fullWidth
                          label={t('previewer.continueUnframed')}
                          onClick={() => continueAndClose(null, otherWorkSlug)}
                          variant="tertiary"
                        />
                      </>
                    )
                  : undefined
              }
            />
          </div>
        ) : null}
      </Modal>
    </>
  );
};
